import * as React from 'react';
import {
  Button,
  makeStyles,
  tokens,
  Text,
  Checkbox,
  Table,
  TableHeader,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  Badge,
} from '@fluentui/react-components';
import { Add20Regular, Delete20Regular } from '@fluentui/react-icons';
import { EventoProjetoCreate } from '../../../types/project';
import { Event } from '../../../types/event';
import { Marker } from '../../../types/marker';
import { getSeverityBadgeColor, getSeverityLabel } from '../../../utils/severityHelpers';
import { MultiselectFilter } from '../../../components/MultiselectFilter';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  table: {
    width: '100%',
    tableLayout: 'fixed', // Importante para larguras percentuais funcionarem
  },
  descriptionCell: {
    width: '25%', // 25% da largura total
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    padding: tokens.spacingVerticalXS,
    verticalAlign: 'middle',
  },
  detailCell: {
    width: '35%', // 35% da largura total
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    padding: tokens.spacingVerticalXS,
    verticalAlign: 'middle',
  },
  markerCell: {
    width: '15%', // 15% da largura total
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    padding: tokens.spacingVerticalXS,
    verticalAlign: 'middle',
  },
  severityCell: {
    width: '15%', // 15% da largura total
    textAlign: 'center',
    padding: tokens.spacingVerticalXS,
    verticalAlign: 'middle',
  },
  actionsCell: {
    width: '10%', // 10% da largura total
    display: 'flex',
    gap: tokens.spacingHorizontalXXS,
    justifyContent: 'flex-end',
    minWidth: '60px', // Mínimo para o botão não ficar muito pequeno
    padding: tokens.spacingVerticalXS,
    verticalAlign: 'middle',
  },
  addSection: {
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    paddingTop: tokens.spacingVerticalM,
    marginTop: tokens.spacingVerticalM,
  },
  addForm: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr auto',
    gap: tokens.spacingHorizontalM,
    alignItems: 'end',
    '@media (max-width: 768px)': {
      gridTemplateColumns: '1fr',
      gap: tokens.spacingVerticalS,
    },
  },
  addButton: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  filterSection: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'flex-start',
    gap: tokens.spacingHorizontalM,
    marginBottom: tokens.spacingVerticalM,
    padding: tokens.spacingVerticalS,
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusMedium,
    '@media (max-width: 768px)': {
      flexDirection: 'column',
      alignItems: 'stretch',
    },
  },
});

interface ProjectEventsGridProps {
  eventosProjetos: EventoProjetoCreate[];
  events: Event[];
  markers: Marker[];
  onChange: (eventos: EventoProjetoCreate[]) => void;
  loading?: boolean;
}

const ProjectEventsGrid: React.FC<ProjectEventsGridProps> = ({
  eventosProjetos,
  events,
  markers,
  onChange,
  loading = false,
}) => {
  const styles = useStyles();
  const [selectedEvents, setSelectedEvents] = React.useState<number[]>([]);
  const [filterMarcadores, setFilterMarcadores] = React.useState<string[]>([]);
  const [filterSeveridades, setFilterSeveridades] = React.useState<string[]>([]);

  const getSeverityColor = (severity: number) => {
    return getSeverityBadgeColor(severity as 0 | 1 | 2 | 3);
  };

  const availableEvents = events.filter(event => {
    // Filtro básico: não incluir eventos já adicionados
    if (eventosProjetos.some(projetoEvento => projetoEvento.eventoId === event.id)) {
      return false;
    }

    // Filtro por marcadores (apenas se houver seleção)
    if (filterMarcadores.length > 0 && !filterMarcadores.includes(event.marcadorId.toString())) {
      return false;
    }

    // Filtro por severidades (apenas se houver seleção)
    if (filterSeveridades.length > 0 && !filterSeveridades.includes(event.severidade.toString())) {
      return false;
    }

    return true;
  });

  const handleSelectEvent = (eventId: number, checked: boolean) => {
    setSelectedEvents(prev => {
      if (checked) {
        // Evitar duplicados
        if (prev.includes(eventId)) {
          return prev;
        }
        return [...prev, eventId];
      } else {
        return prev.filter(id => id !== eventId);
      }
    });
  };

  const handleAddSelectedEvents = () => {
    const newEvents = selectedEvents.map(eventId => {
      const event = events.find(e => e.id === eventId);
      if (!event) {
        return null;
      }

      const mappedEvent = {
        eventoId: event.id,
        descricao: event.descricao,
        detalhe: event.detalhe,
        severidade: event.severidade,
        pontuacao: 0, // Valor padrão inicial
        marcadorId: event.marcadorId,
        consultaERPId: event.consultaERPId,
      };

      return mappedEvent;
    }).filter(Boolean) as EventoProjetoCreate[];

    onChange([...eventosProjetos, ...newEvents]);
    setSelectedEvents([]);
  };

  const handleRemoveEvent = (index: number) => {
    const newEvents = eventosProjetos.filter((_, i) => i !== index);
    onChange(newEvents);
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Text weight="semibold" size={400}>Eventos do Projeto</Text>
        <Text size={200} color="neutralSecondary">
          {eventosProjetos.length} evento{eventosProjetos.length !== 1 ? 's' : ''}
        </Text>
      </div>

      {eventosProjetos.length > 0 && (
        <Table className={styles.table}>
          <TableHeader>
            <TableRow>
              <TableHeaderCell>Descrição</TableHeaderCell>
              <TableHeaderCell>Detalhe</TableHeaderCell>
              <TableHeaderCell>Marcador</TableHeaderCell>
              <TableHeaderCell>Severidade</TableHeaderCell>
              <TableHeaderCell>Ações</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {eventosProjetos.map((event, index) => (
              <TableRow key={index}>
                <TableCell className={styles.descriptionCell}>
                  <Text>{event.descricao}</Text>
                </TableCell>
                <TableCell className={styles.detailCell}>
                  <Text>{event.detalhe}</Text>
                </TableCell>
                <TableCell className={styles.markerCell}>
                  <Text>{markers.find(m => m.id === event.marcadorId)?.descricao || 'Nenhum'}</Text>
                </TableCell>
                <TableCell className={styles.severityCell}>
                  <Badge
                    appearance="filled"
                    color={getSeverityColor(event.severidade)}
                  >
                    {getSeverityLabel(event.severidade as 0 | 1 | 2 | 3)}
                  </Badge>
                </TableCell>
                <TableCell className={styles.actionsCell}>
                  <Button
                    appearance="subtle"
                    icon={<Delete20Regular />}
                    onClick={() => handleRemoveEvent(index)}
                    size="small"
                    disabled={loading}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <div className={styles.addSection}>
        <Text weight="semibold" size={300}>Adicionar Eventos</Text>

        {events.length > 0 && (
          <>
            {/* Filtros */}
            <div className={styles.filterSection}>
              <MultiselectFilter
                label="Marcadores"
                placeholder="Filtrar por marcadores..."
                options={markers.map(marker => ({
                  value: marker.id.toString(),
                  label: marker.descricao,
                }))}
                selectedOptions={filterMarcadores}
                onSelectionChange={setFilterMarcadores}
              />
              <MultiselectFilter
                label="Severidades"
                placeholder="Filtrar por severidades..."
                options={[
                  { value: '0', label: 'Baixa' },
                  { value: '1', label: 'Média' },
                  { value: '2', label: 'Alta' },
                  { value: '3', label: 'Crítica' },
                ]}
                selectedOptions={filterSeveridades}
                onSelectionChange={setFilterSeveridades}
              />
            </div>

            {availableEvents.length > 0 ? (
              <div style={{ marginTop: tokens.spacingVerticalS }}>
                <Text size={200} style={{ marginBottom: tokens.spacingVerticalXS }}>
                  Selecione os eventos que deseja adicionar ({availableEvents.length} disponível{availableEvents.length !== 1 ? 'is' : ''}):
                </Text>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
              gap: tokens.spacingHorizontalM,
              marginBottom: tokens.spacingVerticalM
            }}>
              {availableEvents.map((event) => (
                <div key={event.id} style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: tokens.spacingVerticalXS,
                  padding: tokens.spacingVerticalS,
                  border: `1px solid ${tokens.colorNeutralStroke2}`,
                  borderRadius: tokens.borderRadiusMedium,
                  backgroundColor: selectedEvents.includes(event.id) ? tokens.colorNeutralBackground1Selected : tokens.colorNeutralBackground1,
                  cursor: 'pointer',
                  position: 'relative',
                }}
                onClick={() => handleSelectEvent(event.id, !selectedEvents.includes(event.id))}
                >
                  {/* Tags no canto superior direito */}
                  <div style={{ 
                    position: 'absolute',
                    top: tokens.spacingVerticalS,
                    right: tokens.spacingHorizontalS,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-end',
                    gap: tokens.spacingVerticalXXS,
                  }}>
                    {markers.find(m => m.id === event.marcadorId) && (
                      <Badge
                        appearance="outline"
                        color="informative"
                        size="small"
                      >
                        {markers.find(m => m.id === event.marcadorId)?.descricao}
                      </Badge>
                    )}
                    <Badge
                      appearance="filled"
                      color={getSeverityColor(event.severidade)}
                      size="small"
                    >
                      {getSeverityLabel(event.severidade as 0 | 1 | 2 | 3)}
                    </Badge>
                  </div>

                  <div style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: tokens.spacingHorizontalXS,
                    paddingRight: '100px', // Espaço para as tags
                  }}>
                    <Checkbox
                      checked={selectedEvents.includes(event.id)}
                      onChange={(e, data) => {
                        e.stopPropagation();
                        handleSelectEvent(event.id, data.checked === true);
                      }}
                    />
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS }}>
                      <Text weight="semibold" size={300}>{event.descricao}</Text>
                      {event.detalhe && (
                        <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
                          {event.detalhe}
                        </Text>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
                {selectedEvents.length > 0 && (
                  <Button
                    appearance="primary"
                    icon={<Add20Regular />}
                    onClick={handleAddSelectedEvents}
                    className={styles.addButton}
                    disabled={loading}
                  >
                    Adicionar {selectedEvents.length} Evento{selectedEvents.length !== 1 ? 's' : ''} Selecionado{selectedEvents.length !== 1 ? 's' : ''}
                  </Button>
                )}
              </div>
            ) : (
              <Text size={200} color="neutralSecondary">
                Nenhum evento encontrado com os filtros selecionados.
              </Text>
            )}
          </>
        )}

        {events.length === 0 && (
          <Text size={200} color="neutralSecondary">
            Nenhum evento disponível para adicionar.
          </Text>
        )}
      </div>
    </div>
  );
};

export default ProjectEventsGrid;